// src/providers/AblyProvider.tsx
import { Flex, Loader } from '@mantine/core';
import type { InboundMessage, TokenParams } from 'ably';
import * as Ably from 'ably';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AblyTokenData, getAblyToken } from './ablyTokenManager';
import { useAuthLoading, useAuthLoggedIn, useAuthUser } from './AuthProvider';

import { useHeartRateStore } from '../store/useHeartRateStore';
import {
  exposeAblyDebugGlobals,
  initDebugFlagFromQuery,
} from '../utils/debugFlag';
import { mapPolarHeartRateEvent } from '../utils/mapPolarHeartRateEvent';
import type { SendMessageFunction } from '../utils/sessionEventManager';

type ConnectionStatus = 'connected' | 'connecting' | 'disconnected';

export interface SentEventEntry {
  ts: number;
  event: string;
  data: unknown;
}

const MAX_SENT_EVENTS = 100;

type MessageCallback = (msg: InboundMessage) => void;
type MessageFilter = (msg: InboundMessage) => boolean;

interface AblyContextType {
  connectionStatus: ConnectionStatus;
  sendMessage: SendMessageFunction;
  sentEvents: SentEventEntry[];
  clearSentEvents: () => void;
  subscribeToDeviceMessages: (
    clientId: string,
    callback: MessageCallback
  ) => () => void;
  subscribeToMessages: (
    filter: MessageFilter,
    callback: MessageCallback
  ) => () => void;
}

const AblyContext = createContext<AblyContextType | null>(null);

// -------- normalize inbound (string->object, busta->piatto, aggiunge clientId se manca)
function normalizeInbound(data: any, msgClientId?: string) {
  let payload = data;

  // Converti buffer in stringa se necessario
  // Gestisce: Buffer (Node.js), Uint8Array, ArrayBuffer, o qualsiasi TypedArray
  if (payload && typeof payload !== 'string') {
    try {
      // Node.js Buffer
      if (typeof Buffer !== 'undefined' && Buffer.isBuffer(payload)) {
        payload = payload.toString('utf-8');
      }
      // Uint8Array o altri TypedArray
      else if (payload instanceof Uint8Array) {
        payload = new TextDecoder('utf-8').decode(payload);
      }
      // ArrayBuffer
      else if (payload instanceof ArrayBuffer) {
        payload = new TextDecoder('utf-8').decode(new Uint8Array(payload));
      }
      // Altri TypedArray (Int8Array, Int16Array, etc.)
      else if (
        ArrayBuffer.isView &&
        ArrayBuffer.isView(payload) &&
        !(payload instanceof DataView)
      ) {
        payload = new TextDecoder('utf-8').decode(
          new Uint8Array(payload.buffer, payload.byteOffset, payload.byteLength)
        );
      }
      // Prova a decodificare se ha byteLength (potrebbe essere un buffer non riconosciuto)
      else if (
        payload &&
        typeof payload === 'object' &&
        'byteLength' in payload &&
        (payload as any).byteLength > 0
      ) {
        try {
          // Prova a creare un Uint8Array dal buffer
          const uint8Array = new Uint8Array(
            (payload as any).buffer || payload,
            (payload as any).byteOffset || 0,
            (payload as any).byteLength
          );
          payload = new TextDecoder('utf-8').decode(uint8Array);
        } catch {
          // Ignora errori di decodifica
        }
      }
    } catch {
      // Ignora errori di conversione, continua con il payload originale
    }
  }

  try {
    if (typeof payload === 'string') payload = JSON.parse(payload);
  } catch {
    /* ignore */
  }

  if (payload && typeof payload === 'object' && 'data' in payload) {
    const env = payload as { clientId?: string; data?: any; name?: string };
    return {
      ...(env.data ?? {}),
      // Preserva il name se presente a livello esterno
      ...(env.name ? { name: env.name } : {}),
      clientId:
        env.clientId ??
        (env.data && typeof env.data === 'object'
          ? env.data.clientId
          : undefined) ??
        msgClientId,
    };
  }

  if (payload && typeof payload === 'object') {
    return { ...payload, clientId: (payload as any).clientId ?? msgClientId };
  }

  return { value: payload, clientId: msgClientId };
}

// Helper per estrarre appId con default a 1
const getAppId = (payload: unknown): number => {
  if (payload && typeof payload === 'object' && 'appId' in payload) {
    const appId = (payload as { appId?: unknown }).appId;
    if (typeof appId === 'number') return appId;
    if (typeof appId === 'string') {
      const parsed = Number(appId);
      return Number.isFinite(parsed) ? parsed : 1;
    }
  }
  return 1; // default
};

// Helper per estrarre clientId da vr-event
const extractVrEventClientId = (
  payload: unknown,
  messageClientId: string
): string => {
  if (!payload || typeof payload !== 'object') {
    return messageClientId;
  }

  const appId = getAppId(payload);
  const payloadObj = payload as Record<string, unknown>;

  // Per lifesuite (appId: 2), usa deviceCode
  if (appId === 2 && 'deviceCode' in payloadObj) {
    const deviceCode = payloadObj.deviceCode;
    return typeof deviceCode === 'string' && deviceCode
      ? deviceCode
      : messageClientId;
  }

  // Per altri appId (default 1), usa messageClientId standard
  return messageClientId;
};

// -------- normalize outbound (accetta piatto o busta, pubblica piatto)
// function normalizeOutbound(event: string, dataOrEnvelope: any) {
//   if (
//     dataOrEnvelope &&
//     typeof dataOrEnvelope === 'object' &&
//     'data' in dataOrEnvelope
//   ) {
//     const env = dataOrEnvelope as {
//       name?: string;
//       clientId?: string;
//       data?: any;
//     };
//     const name = env.name ?? event;
//     const payload = { ...(env.data ?? {}), clientId: env.clientId };
//     return { name, data: payload };
//   }
//   return { name: event, data: dataOrEnvelope };
// }

export const AblyProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const user = useAuthUser();
  const isLoggedIn = useAuthLoggedIn();
  const isLoading = useAuthLoading();
  const { addEvent } = useHeartRateStore();

  const [ably, setAbly] = useState<Ably.Realtime | null>(null);
  const [status, setStatus] = useState<ConnectionStatus>('disconnected');
  const [initializing, setInitializing] = useState(true);

  // de-duplica messaggi
  const processedMessageIdsRef = useRef<Set<string>>(new Set());
  const clearProcessedIds = () => processedMessageIdsRef.current.clear();

  // Message subscription registry
  // Map<clientId, Set<callbacks>> for device-specific subscriptions
  const deviceSubscriptionsRef = useRef<Map<string, Set<MessageCallback>>>(
    new Map()
  );
  // Set of { filter, callback } for generic subscriptions
  const genericSubscriptionsRef = useRef<
    Set<{ filter: MessageFilter; callback: MessageCallback }>
  >(new Set());

  // 🔧 Init flag (querystring) + helpers globali una sola volta
  useEffect(() => {
    initDebugFlagFromQuery();
    exposeAblyDebugGlobals();
  }, []);

  // token
  const fetchAblyToken = async (): Promise<AblyTokenData> => {
    const resp = await fetch(`${import.meta.env.VITE_API_URL}/services/ably`, {
      method: 'GET',
      credentials: 'include',
    });
    if (!resp.ok) throw new Error('Failed to fetch Ably token');
    return resp.json();
  };

  useEffect(() => {
    let inst: Ably.Realtime | null = null;
    let unsubMessages: (() => void) | null = null;

    const init = async () => {
      setInitializing(true);
      if (!isLoggedIn || !user) {
        setInitializing(false);
        return;
      }

      try {
        setStatus('connecting');
        const tokenData = await getAblyToken(fetchAblyToken);

        inst = new Ably.Realtime({
          authCallback: (_params: TokenParams, cb) => cb(null, tokenData),
        });

        inst.connection.on(state => {
          if (state.current === 'connected') {
            setStatus('connected');
            wireChannel(inst!, user!.id.toString());
          } else if (
            state.current === 'failed' ||
            state.current === 'suspended'
          ) {
            setStatus('disconnected');
          }
        });

        setAbly(inst);
      } catch {
        setStatus('disconnected');
      } finally {
        setInitializing(false);
      }
    };

    const wireChannel = (client: Ably.Realtime, userId: string) => {
      const channel = client.channels.get(`private:${userId}`);

      // Presence enter/leave - handled by useDeviceConnectionsStatus hook
      // Presence bootstrap is now handled by components using the hook

      // Messages
      const onMessage = (message: InboundMessage) => {
        // Log tutti gli eventi Ably in arrivo (incoming)
        console.log('[Ably IN]', {
          name: message.name,
          id: message.id,
          clientId: message.clientId,
          timestamp: message.timestamp,
          data: message.data,
        });
        // de-duplica
        if (message.id && processedMessageIdsRef.current.has(message.id)) {
          return;
        }
        if (message.id) processedMessageIdsRef.current.add(message.id);

        const payload = normalizeInbound(message.data, message.clientId);

        // Estrai il name dal payload se presente (per messaggi come vr-event)
        const messageName = (payload as any)?.name || message.name;

        const normalizedMessage = {
          ...message,
          name: messageName,
          data: payload,
        } as InboundMessage;

        // Notify device-specific subscribers
        // Per vr-event, usa extractVrEventClientId per gestire il formato lifesuite
        let messageClientId = (payload as any)?.clientId || message.clientId;
        if (messageName === 'vr-event') {
          messageClientId = extractVrEventClientId(payload, messageClientId);
        }

        if (messageClientId) {
          const deviceCallbacks =
            deviceSubscriptionsRef.current.get(messageClientId);
          if (deviceCallbacks) {
            deviceCallbacks.forEach(cb => {
              try {
                cb(normalizedMessage);
              } catch (error) {
                console.error(
                  '[AblyProvider] Error in device message callback:',
                  error
                );
              }
            });
          }
        }

        // Notify generic subscribers
        genericSubscriptionsRef.current.forEach(({ filter, callback }) => {
          try {
            if (filter(normalizedMessage)) {
              callback(normalizedMessage);
            }
          } catch (error) {
            console.error('Error in generic message callback:', error);
          }
        });

        // inoltra allo store HR SOLO se c'è un heartRate valido
        if (message.name === 'heartRate') {
          const payloadData = payload as Record<string, unknown>;
          const eventData = mapPolarHeartRateEvent({
            payload: payloadData,
            messageId: message.id ?? `${Date.now()}`,
            messageClientId: message.clientId,
            messageTimestamp: message.timestamp,
          });

          if (eventData) {
            addEvent(eventData);
          } else {
            // Messaggio con solo currentState (phase dal dispositivo): aggiorna phase nello store
            const nested = payloadData?.data as
              | Record<string, unknown>
              | undefined;
            const currentState = (payloadData?.currentState ??
              nested?.currentState) as string | undefined;
            const validPhases = ['p2', 'p1', 'p0', 'p-1', 'p-2'];
            if (
              typeof currentState === 'string' &&
              validPhases.includes(currentState)
            ) {
              const heartRateStore = useHeartRateStore.getState();
              heartRateStore.setPhase(currentState);
              const latestHr = heartRateStore.getLatestHeartRate() ?? 0;
              heartRateStore.setPhaseEvent(currentState, latestHr);
            }
          }
        }
        // vr-event and Connected messages are now handled by useDeviceConnectionsStatus hook
      };

      channel.subscribe(onMessage);
      unsubMessages = () => channel.unsubscribe(onMessage);
    };

    init();

    return () => {
      try {
        if (unsubMessages) unsubMessages();
        clearProcessedIds();
        // Clear subscriptions on cleanup
        deviceSubscriptionsRef.current.clear();
        genericSubscriptionsRef.current.clear();
        //resetDevicesState();
        if (inst) {
          inst.close();
          setAbly(null);
          setStatus('disconnected');
        }
      } catch {
        // Ignore error
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, user?.id]);

  const [sentEvents, setSentEvents] = useState<SentEventEntry[]>([]);

  const clearSentEvents = useCallback(() => {
    setSentEvents([]);
  }, []);

  // publish (accetta payload piatto o busta)
  const sendMessage = useCallback<SendMessageFunction>(
    (event: string, data: unknown) => {
      if (!ably || ably.connection.state !== 'connected' || !user) {
        console.warn('[ably] publish skipped', {
          event,
          payload: data,
          connected: ably?.connection.state === 'connected',
          userId: user?.id ?? null,
        });
        return;
      }

      console.info('[ably] publish', {
        channel: `private:${user.id}`,
        event,
        payload: data,
      });

      setSentEvents(prev => {
        const entry: SentEventEntry = { ts: Date.now(), event, data };
        const next = [...prev, entry].slice(-MAX_SENT_EVENTS);
        return next;
      });

      const channel = ably.channels.get(`private:${user.id}`);
      channel.publish({ name: event, data: JSON.stringify(data) });
    },
    [ably, user]
  );

  const subscribeToDeviceMessages = useCallback(
    (clientId: string, callback: MessageCallback): (() => void) => {
      if (!deviceSubscriptionsRef.current.has(clientId)) {
        deviceSubscriptionsRef.current.set(clientId, new Set());
      }
      const callbacks = deviceSubscriptionsRef.current.get(clientId)!;
      callbacks.add(callback);

      return () => {
        const currentCallbacks = deviceSubscriptionsRef.current.get(clientId);
        if (currentCallbacks) {
          currentCallbacks.delete(callback);
          if (currentCallbacks.size === 0) {
            deviceSubscriptionsRef.current.delete(clientId);
          }
        }
      };
    },
    []
  );

  const subscribeToMessages = useCallback(
    (filter: MessageFilter, callback: MessageCallback): (() => void) => {
      const subscription = { filter, callback };
      genericSubscriptionsRef.current.add(subscription);

      return () => {
        genericSubscriptionsRef.current.delete(subscription);
      };
    },
    []
  );

  const value = useMemo<AblyContextType>(
    () => ({
      connectionStatus: status,
      sendMessage,
      sentEvents,
      clearSentEvents,
      subscribeToDeviceMessages,
      subscribeToMessages,
    }),
    [
      status,
      sendMessage,
      sentEvents,
      clearSentEvents,
      subscribeToDeviceMessages,
      subscribeToMessages,
    ]
  );

  if (isLoading || (isLoggedIn && initializing)) {
    return (
      <Flex align='center' justify='center' style={{ minHeight: '25vh' }}>
        <Loader />
      </Flex>
    );
  }

  return <AblyContext.Provider value={value}>{children}</AblyContext.Provider>;
};

export const useAbly = () => {
  const ctx = useContext(AblyContext);
  if (!ctx) throw new Error('useAbly must be used within AblyProvider');
  return ctx;
};
